import { uploadFile } from "@/core/helpers/rest/file";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import {
	type AutocompleteFetcher,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	type FileRecord,
	FormBuilder,
} from "@k3-tech/react-kit";

export interface GroupProductFormValues {
	productID: string;
	salesChannelCategoryID: string;
	priceType: string;
	customPrice: number;
	menuImage: FileRecord[];
	isActive: boolean;
}

export interface SalesChannelGroupProductFormDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	mode: "create" | "edit";
	categoryOptions: { value: string; label: string }[];
	productAutocompleteFetcher: AutocompleteFetcher;
	initialProduct?: { value: string; label: string };
	defaultValues?: Partial<GroupProductFormValues>;
	onSubmit: (values: GroupProductFormValues) => unknown;
}

// Upload to masterdata and keep the proxied URL so Grab can render it.
const uploadMenuImage = async (file: File): Promise<Partial<FileRecord>> => {
	const result = await uploadFile(file);
	const uploaded = result?.files?.[0];
	if (!uploaded) return {};
	return {
		id: uploaded.file_id,
		name: uploaded.original_file_name,
		thumbnailUrl: uploaded.image_proxy_urls?.medium ?? uploaded.image_proxy_url,
		url: uploaded.image_proxy_url ?? uploaded.public_url,
		type: uploaded.mime_type,
		status: "success",
	};
};

export function SalesChannelGroupProductFormDialog({
	open,
	onOpenChange,
	mode,
	categoryOptions,
	productAutocompleteFetcher,
	initialProduct,
	defaultValues,
	onSubmit,
}: SalesChannelGroupProductFormDialogProps) {
	const { isSubmitting, guardedSubmit } = useSubmitGuard(onSubmit);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>
						{mode === "create" ? "Add Product" : "Edit Product"}
					</DialogTitle>
				</DialogHeader>
				<FormBuilder<GroupProductFormValues>
					onSubmit={guardedSubmit}
					isSubmitting={isSubmitting}
					onCancel={() => onOpenChange(false)}
					defaultValues={defaultValues}
					actionsClassName="justify-end"
					showActionsSeparator={false}
					sections={[
						{
							layout: "grid",
							grid: { cols: 1, gap: "gap-4" },
							fields: [
								{
									name: "productID",
									label: "Product",
									type: "autocomplete",
									options: [],
									autocompleteMode: "server",
									fetcher: productAutocompleteFetcher,
									required: true,
									// One membership per product per group — lock it on edit.
									disabled: mode === "edit",
									defaultValue: defaultValues?.productID,
									initialSelectedOptions: initialProduct
										? [initialProduct]
										: undefined,
								},
								{
									name: "salesChannelCategoryID",
									label: "Category",
									type: "select",
									options: categoryOptions,
									placeholder: "Select category",
									required: true,
									defaultValue: null,
									className: "w-full",
								},
								// Custom price is temporarily hidden from the UI; memberships default to pricelist.
								{
									name: "menuImage",
									label: "Custom Menu Image",
									type: "file",
									fileUploader: uploadMenuImage,
									fileMaxFiles: 1,
									fileWithImagePreview: true,
									fileAccept: {
										"image/*": [".png", ".jpg", ".jpeg", ".webp", ".gif"],
									},
									defaultValue: defaultValues?.menuImage ?? [],
								},
							],
						},
						{
							layout: "flex",
							fields: [
								{
									name: "isActive",
									label: "Active",
									type: "switch",
									defaultValue: true,
								},
							],
						},
					]}
					submitLabel={mode === "create" ? "Add" : "Save"}
				/>
			</DialogContent>
		</Dialog>
	);
}
